// src/components/dashboard/cases/newCase/case_id/caseTable/hooks/useCrossSection2D.js
import { useEffect, useRef } from "react";
import {
  XS_MEASURE_LINE_COLOR,
  XS_MEASURE_LINE_HALO,
  XS_MEASURE_POINT_RADIUS,
  XS_MEASURE_HIT_RADIUS,
  XS_MEASURE_LINE_WIDTH,
  XS_SNAP_RADIUS_PX,
  XS_SNAP_VERTEX_PX,
  XS_MEASURE_LABEL_COLOR,
  XS_MEASURE_LABEL_HALO,
  MEASURE_UNIT,
} from "../constants";

export const useCrossSection2D = ({
  crossSectionOutline,
  xsMeasureVersion,
  crossSectionCanvasRef,
  setXsMeasureVersion,
}) => {
  const xsMeasurePointsRef = useRef([]);
  const xsMeasureDragRef = useRef(null);
  const xsTransformRef = useRef(null);
  const xsViewRef = useRef({ zoom: 1, panX: 0, panY: 0 });
  const xsPanDragRef = useRef(null);

  useEffect(() => {
    xsMeasurePointsRef.current = [];
    xsMeasureDragRef.current = null;
    xsPanDragRef.current = null;
    xsViewRef.current = { zoom: 1, panX: 0, panY: 0 };
    setXsMeasureVersion((v) => v + 1);
  }, [crossSectionOutline, setXsMeasureVersion]);

  useEffect(() => {
    const canvas = crossSectionCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animFrame = null;
    let needsRedraw = true;

    const draw = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = canvas.getBoundingClientRect();
      const W = Math.max(rect.width, 10);
      const H = Math.max(rect.height, 10);
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);

      if (!crossSectionOutline) {
        ctx.fillStyle = "rgba(20, 30, 45, 0.35)";
        ctx.fillRect(0, 0, W, H);
        xsTransformRef.current = null;
        return;
      }

      ctx.fillStyle = "rgba(20, 30, 45, 0.28)";
      ctx.fillRect(0, 0, W, H);

      const { segments, bounds, axisX, axisY } = crossSectionOutline;
      const bw = bounds.maxX - bounds.minX || 1;
      const bh = bounds.maxY - bounds.minY || 1;

      const pad = 0.08;
      const fitScale = Math.min(
        (W * (1 - pad * 2)) / bw,
        (H * (1 - pad * 2)) / bh
      );

      const view = xsViewRef.current;
      const scale = fitScale * view.zoom;
      const cx = (bounds.minX + bounds.maxX) / 2;
      const cy = (bounds.minY + bounds.maxY) / 2;

      const toScreen = (x, y) => [
        W / 2 + (x - cx) * scale + view.panX,
        H / 2 - (y - cy) * scale + view.panY,
      ];

      xsTransformRef.current = {
        toScreen,
        toWorld: (sx, sy) => [
          (sx - W / 2 - view.panX) / scale + cx,
          -(sy - H / 2 - view.panY) / scale + cy,
        ],
        scale,
        fitScale,
        W,
        H,
        cx,
        cy,
      };

      ctx.strokeStyle = "rgba(255,255,255,0.07)";
      ctx.lineWidth = 1;
      const stepX = Math.max(1, Math.round(bw / 8));
      const stepY = Math.max(1, Math.round(bh / 8));
      for (let gx = Math.ceil(bounds.minX); gx <= bounds.maxX; gx += stepX) {
        const [sx] = toScreen(gx, 0);
        if (sx < -50 || sx > W + 50) continue;
        ctx.beginPath(); ctx.moveTo(sx, 0); ctx.lineTo(sx, H); ctx.stroke();
      }
      for (let gy = Math.ceil(bounds.minY); gy <= bounds.maxY; gy += stepY) {
        const [, sy] = toScreen(0, gy);
        if (sy < -50 || sy > H + 50) continue;
        ctx.beginPath(); ctx.moveTo(0, sy); ctx.lineTo(W, sy); ctx.stroke();
      }

      if (axisX && axisY) {
        ctx.strokeStyle = "rgba(255,255,255,0.4)";
        ctx.lineWidth = 1;
        const [x0, y0] = toScreen(axisX.from.x, axisX.from.y);
        const [x1, y1] = toScreen(axisX.to.x, axisX.to.y);
        ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x1, y1); ctx.stroke();
        const [x2, y2] = toScreen(axisY.from.x, axisY.from.y);
        const [x3, y3] = toScreen(axisY.to.x, axisY.to.y);
        ctx.beginPath(); ctx.moveTo(x2, y2); ctx.lineTo(x3, y3); ctx.stroke();
      }

      ctx.strokeStyle = "#7fe0ff";
      ctx.lineWidth = 1.6;
      ctx.lineCap = "round";
      ctx.beginPath();
      for (const s of segments) {
        const [sx1, sy1] = toScreen(s.x1, s.y1);
        const [sx2, sy2] = toScreen(s.x2, s.y2);
        ctx.moveTo(sx1, sy1);
        ctx.lineTo(sx2, sy2);
      }
      ctx.stroke();

      const [bx1, by1] = toScreen(bounds.minX, bounds.maxY);
      const [bx2, by2] = toScreen(bounds.maxX, bounds.minY);
      ctx.strokeStyle = "rgba(127, 224, 255, 0.35)";
      ctx.setLineDash([4, 4]);
      ctx.lineWidth = 1;
      ctx.strokeRect(bx1, by1, bx2 - bx1, by2 - by1);
      ctx.setLineDash([]);

      const pts = xsMeasurePointsRef.current;
      if (pts.length > 0) {
        if (pts.length === 2) {
          const [sx1, sy1] = toScreen(pts[0][0], pts[0][1]);
          const [sx2, sy2] = toScreen(pts[1][0], pts[1][1]);

          ctx.strokeStyle = XS_MEASURE_LINE_HALO;
          ctx.lineWidth = XS_MEASURE_LINE_WIDTH + 3;
          ctx.beginPath();
          ctx.moveTo(sx1, sy1);
          ctx.lineTo(sx2, sy2);
          ctx.stroke();

          ctx.strokeStyle = XS_MEASURE_LINE_COLOR;
          ctx.lineWidth = XS_MEASURE_LINE_WIDTH;
          ctx.beginPath();
          ctx.moveTo(sx1, sy1);
          ctx.lineTo(sx2, sy2);
          ctx.stroke();

          const mx = (sx1 + sx2) / 2;
          const my = (sy1 + sy2) / 2;
          const dx = pts[1][0] - pts[0][0];
          const dy = pts[1][1] - pts[0][1];
          const dist = Math.sqrt(dx * dx + dy * dy);
          const labelText = dist.toFixed(3) + " " + MEASURE_UNIT;

          ctx.font = "bold 11px 'SF Mono', Consolas, monospace";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";

          ctx.strokeStyle = XS_MEASURE_LABEL_HALO;
          ctx.lineWidth = 3;
          ctx.strokeText(labelText, mx, my - 22);

          ctx.fillStyle = XS_MEASURE_LABEL_COLOR;
          ctx.fillText(labelText, mx, my - 22);

          ctx.textAlign = "start";
          ctx.textBaseline = "alphabetic";
        }

        for (let i = 0; i < pts.length; i++) {
          const [sx, sy] = toScreen(pts[i][0], pts[i][1]);
          ctx.fillStyle = XS_MEASURE_LINE_HALO;
          ctx.beginPath();
          ctx.arc(sx, sy, XS_MEASURE_POINT_RADIUS + 2, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = XS_MEASURE_LINE_COLOR;
          ctx.beginPath();
          ctx.arc(sx, sy, XS_MEASURE_POINT_RADIUS, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      if (Math.abs(view.zoom - 1) > 0.01 || view.panX !== 0 || view.panY !== 0) {
        ctx.fillStyle = "rgba(8, 20, 38, 0.75)";
        ctx.strokeStyle = "rgba(127, 224, 255, 0.5)";
        ctx.lineWidth = 1;
        const badgeW = 90;
        const badgeH = 22;
        const badgeX = W - badgeW - 6;
        const badgeY = 6;
        ctx.beginPath();
        ctx.roundRect
          ? ctx.roundRect(badgeX, badgeY, badgeW, badgeH, 4)
          : ctx.rect(badgeX, badgeY, badgeW, badgeH);
        ctx.fill();
        ctx.stroke();

        ctx.fillStyle = "#7fe0ff";
        ctx.font = "bold 10px 'SF Mono', Consolas, monospace";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(
          "Zoom " + (view.zoom * 100).toFixed(0) + "%",
          badgeX + badgeW / 2,
          badgeY + badgeH / 2 + 1
        );
        ctx.textAlign = "start";
        ctx.textBaseline = "alphabetic";
      }

      ctx.strokeStyle = "rgba(255,255,255,0.15)";
      ctx.strokeRect(0.5, 0.5, W - 1, H - 1);
    };

    const scheduleDraw = () => {
      needsRedraw = true;
      if (!animFrame) {
        animFrame = requestAnimationFrame(() => {
          animFrame = null;
          if (needsRedraw) {
            needsRedraw = false;
            draw();
          }
        });
      }
    };

    draw();

    const getCanvasCoords = (ev) => {
      const rect = canvas.getBoundingClientRect();
      return [ev.clientX - rect.left, ev.clientY - rect.top];
    };

    const screenToWorld = (sx, sy) => {
      const t = xsTransformRef.current;
      if (!t) return null;
      const v = xsViewRef.current;
      const sc = t.fitScale * v.zoom;
      return [
        (sx - t.W / 2 - v.panX) / sc + t.cx,
        -(sy - t.H / 2 - v.panY) / sc + t.cy,
      ];
    };

    const snapToOutline = (sx, sy, wx, wy) => {
      const t = xsTransformRef.current;
      if (!t || !crossSectionOutline) return [wx, wy];
      const segs = crossSectionOutline.segments;
      const v = xsViewRef.current;
      const sc = t.fitScale * v.zoom;
      const ox = t.W / 2 - t.cx * sc + v.panX;
      const oy = t.H / 2 + t.cy * sc + v.panY;
      const R = XS_SNAP_RADIUS_PX;
      const VR2 = XS_SNAP_VERTEX_PX * XS_SNAP_VERTEX_PX;

      let bestVd = VR2;
      let bestV = null;
      let bestEd = R * R;
      let bestE = null;

      for (let i = 0; i < segs.length; i++) {
        const s = segs[i];
        const ax = ox + s.x1 * sc;
        const ay = oy - s.y1 * sc;
        const bx = ox + s.x2 * sc;
        const by = oy - s.y2 * sc;

        if (ax < sx - R && bx < sx - R) continue;
        if (ax > sx + R && bx > sx + R) continue;
        if (ay < sy - R && by < sy - R) continue;
        if (ay > sy + R && by > sy + R) continue;

        const dax = sx - ax, day = sy - ay;
        const dbx = sx - bx, dby = sy - by;
        const da2 = dax * dax + day * day;
        const db2 = dbx * dbx + dby * dby;
        if (da2 < bestVd) { bestVd = da2; bestV = [s.x1, s.y1]; }
        if (db2 < bestVd) { bestVd = db2; bestV = [s.x2, s.y2]; }

        const ex = bx - ax, ey = by - ay;
        const len2 = ex * ex + ey * ey;
        let u = len2 > 1e-12 ? (dax * ex + day * ey) / len2 : 0;
        u = u < 0 ? 0 : u > 1 ? 1 : u;
        const px = ax + ex * u - sx;
        const py = ay + ey * u - sy;
        const d2 = px * px + py * py;
        if (d2 < bestEd) {
          bestEd = d2;
          bestE = [s.x1 + (s.x2 - s.x1) * u, s.y1 + (s.y2 - s.y1) * u];
        }
      }

      return bestV || bestE || [wx, wy];
    };

    const hitTestMeasurePoint = (sx, sy) => {
      const t = xsTransformRef.current;
      if (!t) return -1;
      const pts = xsMeasurePointsRef.current;
      for (let i = 0; i < pts.length; i++) {
        const [px, py] = t.toScreen(pts[i][0], pts[i][1]);
        const dx = sx - px;
        const dy = sy - py;
        if (dx * dx + dy * dy <= XS_MEASURE_HIT_RADIUS * XS_MEASURE_HIT_RADIUS) {
          return i;
        }
      }
      return -1;
    };

    const onPointerDown = (ev) => {
      if (!crossSectionOutline) return;

      const isPanButton = ev.button === 1 || ev.button === 2;
      const [sx, sy] = getCanvasCoords(ev);

      if (isPanButton) {
        ev.preventDefault();
        ev.stopPropagation();
        xsPanDragRef.current = {
          startX: sx,
          startY: sy,
          panX0: xsViewRef.current.panX,
          panY0: xsViewRef.current.panY,
        };
        canvas.setPointerCapture?.(ev.pointerId);
        canvas.style.cursor = "grabbing";
        return;
      }

      if (ev.button !== 0) return;

      ev.preventDefault();
      ev.stopPropagation();

      const hitIdx = hitTestMeasurePoint(sx, sy);
      if (hitIdx >= 0) {
        xsMeasureDragRef.current = hitIdx;
        canvas.setPointerCapture?.(ev.pointerId);
        canvas.style.cursor = "grabbing";
        return;
      }

      const w0 = screenToWorld(sx, sy);
      if (!w0) return;
      const [wx, wy] = ev.altKey ? w0 : snapToOutline(sx, sy, w0[0], w0[1]);

      const pts = xsMeasurePointsRef.current;
      if (pts.length >= 2) pts.length = 0;
      pts.push([wx, wy]);

      scheduleDraw();
    };

    const onPointerMove = (ev) => {
      if (xsPanDragRef.current) {
        const [sx, sy] = getCanvasCoords(ev);
        const d = xsPanDragRef.current;
        xsViewRef.current.panX = d.panX0 + (sx - d.startX);
        xsViewRef.current.panY = d.panY0 + (sy - d.startY);
        scheduleDraw();
        return;
      }

      if (xsMeasureDragRef.current === null) return;
      if (!crossSectionOutline) return;

      const [sx, sy] = getCanvasCoords(ev);
      const w0 = screenToWorld(sx, sy);
      if (!w0) return;
      const [wx, wy] = ev.altKey ? w0 : snapToOutline(sx, sy, w0[0], w0[1]);

      xsMeasurePointsRef.current[xsMeasureDragRef.current] = [wx, wy];
      scheduleDraw();
    };

    const onPointerUp = (ev) => {
      if (xsPanDragRef.current) {
        xsPanDragRef.current = null;
        canvas.style.cursor = "grab";
        canvas.releasePointerCapture?.(ev.pointerId);
        return;
      }
      if (xsMeasureDragRef.current === null) return;
      xsMeasureDragRef.current = null;
      canvas.style.cursor = "crosshair";
      canvas.releasePointerCapture?.(ev.pointerId);
    };

    const onHover = (ev) => {
      if (xsPanDragRef.current) return;
      if (!crossSectionOutline) {
        canvas.style.cursor = "default";
        return;
      }
      const [sx, sy] = getCanvasCoords(ev);
      const hitIdx = hitTestMeasurePoint(sx, sy);
      canvas.style.cursor = hitIdx >= 0 ? "grab" : "crosshair";
    };

    const onWheel = (ev) => {
      if (!crossSectionOutline) return;
      ev.preventDefault();
      ev.stopPropagation();

      const rect = canvas.getBoundingClientRect();
      const sx = ev.clientX - rect.left;
      const sy = ev.clientY - rect.top;

      const t = xsTransformRef.current;
      if (!t) return;

      const view = xsViewRef.current;
      const oldZoom = view.zoom;
      const factor = ev.deltaY < 0 ? 1.15 : 1 / 1.15;
      const newZoom = Math.max(0.1, Math.min(50, oldZoom * factor));
      if (newZoom === oldZoom) return;

      const { W, H, cx, cy, fitScale } = t;
      const oldScale = fitScale * oldZoom;
      const wx = (sx - W / 2 - view.panX) / oldScale + cx;
      const wy = -(sy - H / 2 - view.panY) / oldScale + cy;

      view.zoom = newZoom;

      const newScale = fitScale * newZoom;
      view.panX = sx - W / 2 - (wx - cx) * newScale;
      view.panY = sy - H / 2 + (wy - cy) * newScale;

      scheduleDraw();
    };

    const onDblClick = (ev) => {
      if (!crossSectionOutline) return;
      ev.preventDefault();
      xsViewRef.current.zoom = 1;
      xsViewRef.current.panX = 0;
      xsViewRef.current.panY = 0;
      scheduleDraw();
    };

    canvas.addEventListener("pointerdown", onPointerDown);
    canvas.addEventListener("pointermove", onPointerMove);
    canvas.addEventListener("pointerup", onPointerUp);
    canvas.addEventListener("pointercancel", onPointerUp);
    canvas.addEventListener("pointermove", onHover);
    canvas.addEventListener("wheel", onWheel, { passive: false });
    canvas.addEventListener("dblclick", onDblClick);
    canvas.addEventListener("contextmenu", (e) => {
      if (crossSectionOutline) e.preventDefault();
    });

    let ro = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => scheduleDraw());
      ro.observe(canvas);
    }

    const prevHandler = canvas.__xsScheduleDraw;
    canvas.__xsScheduleDraw = scheduleDraw;

    return () => {
      if (animFrame) cancelAnimationFrame(animFrame);
      if (ro) ro.disconnect();
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointermove", onPointerMove);
      canvas.removeEventListener("pointerup", onPointerUp);
      canvas.removeEventListener("pointercancel", onPointerUp);
      canvas.removeEventListener("pointermove", onHover);
      canvas.removeEventListener("wheel", onWheel);
      canvas.removeEventListener("dblclick", onDblClick);
      if (canvas.__xsScheduleDraw === scheduleDraw) {
        canvas.__xsScheduleDraw = prevHandler;
      }
    };
  }, [crossSectionOutline, xsMeasureVersion, crossSectionCanvasRef]);

  return {
    xsMeasurePointsRef,
    xsMeasureDragRef,
    xsViewRef,
    xsPanDragRef,
  };
};